import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { PageLayout } from '../components/layout/Navbar';
import { apiService } from '../services/api.service';
import toast from 'react-hot-toast';
import { Card, Spinner } from '../components/ui';

const Support = () => {
  const { t } = useTranslation();
  const [showSOSModal, setShowSOSModal] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const handleSOS = async () => {
    setIsSending(true);
    try {
      await apiService.sendSOS({
        message: "FAVQULODDA SOS SIGNAL",
        location: "GPS" // In real app, get browser geolocation
      });
      toast.success(t("support.sos.success"));
      setShowSOSModal(false);
    } catch (err) {
      toast.error(t("support.sos.error"));
    } finally {
      setIsSending(false);
    }
  };

  const faq = useMemo(() => [
    { q: t("support.faq.delivery.q"), a: t("support.faq.delivery.a") },
    { q: t("support.faq.debt.q"), a: t("support.faq.debt.a") },
    { q: t("support.faq.quality.q"), a: t("support.faq.quality.a") },
  ], [t]);

  return (
    <PageLayout>
      <div className="max-w-6xl mx-auto flex flex-col gap-12">
          <motion.header 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col gap-2"
          >
            <h1 className="text-4xl font-black tracking-tighter italic uppercase">{t("support.title")}</h1>
            <p className="text-slate-500 font-bold italic uppercase text-[10px] tracking-widest opacity-70">{t("support.subtitle")}</p>
          </motion.header>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* SOS Section */}
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="lg:col-span-5 p-10 bg-red-50/50 dark:bg-red-900/10 rounded-[40px] border border-red-100 dark:border-red-900/20 flex flex-col items-center text-center gap-8 shadow-xl shadow-red-500/5"
            >
              <div className="h-24 w-24 bg-red-100 dark:bg-red-900/30 rounded-[32px] flex items-center justify-center text-red-600 shadow-lg shadow-red-500/10">
                <span className="material-symbols-outlined text-5xl animate-pulse fill-1">emergency</span>
              </div>
              <div className="space-y-3">
                <h3 className="text-2xl font-black text-red-600 italic uppercase tracking-tight">{t("support.sos.title")}</h3>
                <p className="text-red-600/70 dark:text-red-400/60 font-bold italic uppercase text-[10px] tracking-widest leading-relaxed">
                  {t("support.sos.subtitle")}
                </p>
              </div>
              <button 
                onClick={() => setShowSOSModal(true)}
                className="w-full h-16 bg-red-600 text-white rounded-2xl font-black uppercase tracking-[0.2em] text-sm shadow-2xl shadow-red-600/40 hover:bg-red-700 transition-all active:scale-95"
              >
                {t("support.sos.button")}
              </button>
            </motion.div>

            {/* Support Channels */}
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6">
               <SupportCard 
                 icon="chat" 
                 title={t("support.channels.chat.title")} 
                 desc={t("support.channels.chat.desc")} 
                 color="emerald"
                 delay={0.2}
               />
               <SupportCard 
                 icon="call" 
                 title={t("support.channels.call.title")} 
                 desc={t("support.channels.call.desc")} 
                 color="blue"
                 delay={0.3}
               />
               <SupportCard 
                 icon="campaign" 
                 title={t("support.channels.complaint.title")} 
                 desc={t("support.channels.complaint.desc")} 
                 color="purple"
                 delay={0.4}
               />
               <SupportCard 
                 icon="mail" 
                 title={t("support.channels.email.title")} 
                 desc="info@smartgaz.uz" 
                 color="amber"
                 delay={0.5}
               />
            </div>
          </div>

          {/* FAQ Section */}
          <section className="mt-8">
            <div className="flex items-center gap-3 mb-10">
              <div className="h-2 w-10 bg-[#1e9f6b] rounded-full" />
              <h2 className="text-2xl font-black italic uppercase tracking-tight">{t("support.faq.title")}</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {faq.map((item, i) => (
                <motion.div 
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 + i * 0.1 }}
                >
                  <Card className="p-8 bg-white dark:bg-slate-900 rounded-[32px] border-slate-100 dark:border-slate-800/50 hover:border-[#1e9f6b]/30 transition-all group">
                    <h4 className="text-sm font-black text-slate-900 dark:text-white mb-3 italic uppercase tracking-tight group-hover:text-[#1e9f6b] transition-colors">{item.q}</h4>
                    <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest leading-relaxed">{item.a}</p>
                  </Card>
                </motion.div>
              ))}
            </div>
          </section>
      </div>

      {/* SOS Modal */}
      <AnimatePresence>
        {showSOSModal && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-xl flex items-center justify-center p-4"
          >
            <motion.div 
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              className="bg-white dark:bg-slate-900 w-full max-w-[440px] rounded-[40px] p-10 flex flex-col items-center text-center gap-8 border-4 border-red-600 shadow-2xl"
            >
              <div className="h-24 w-24 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center text-red-600">
                <span className="material-symbols-outlined text-6xl fill-1">report_problem</span>
              </div>
              <div className="space-y-4">
                <h2 className="text-3xl font-black text-red-600 italic uppercase tracking-tight">{t("common.confirm")}</h2>
                <p className="text-slate-500 font-bold italic uppercase text-[10px] tracking-widest leading-relaxed px-4">
                  {t("support.sos.confirm")}
                </p>
              </div>
              <div className="flex flex-col w-full gap-4">
                <button 
                  disabled={isSending}
                  onClick={handleSOS}
                  className="h-16 bg-red-600 text-white rounded-2xl font-black uppercase tracking-[0.2em] text-sm shadow-xl shadow-red-600/30 flex items-center justify-center gap-3 active:scale-95 transition-all"
                >
                  {isSending ? <Spinner size="sm" color="white" /> : t("common.submit")}
                </button>
                <button 
                  onClick={() => setShowSOSModal(false)}
                  className="h-16 bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400 rounded-2xl font-black uppercase tracking-[0.2em] text-xs hover:bg-slate-100 dark:hover:bg-slate-700 transition-all"
                >
                  {t("common.cancel")}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </PageLayout>
  );
};

const SupportCard = ({ icon, title, desc, color, delay }) => {
  const colors = {
    emerald: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600',
    blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600',
    purple: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600',
    amber: 'bg-amber-50 dark:bg-amber-900/20 text-amber-600'
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
    >
      <div className="p-8 bg-white dark:bg-slate-900 rounded-[32px] border border-slate-100 dark:border-slate-800/50 flex flex-col gap-6 hover:shadow-xl hover:border-[#1e9f6b]/20 transition-all cursor-pointer group">
        <div className={`size-16 rounded-[20px] flex items-center justify-center ${colors[color]} group-hover:scale-110 transition-transform duration-500`}>
          <span className="material-symbols-outlined text-3xl">{icon}</span>
        </div>
        <div className="space-y-1">
          <h4 className="font-black text-slate-900 dark:text-white italic uppercase tracking-tight group-hover:text-[#1e9f6b] transition-colors">{title}</h4>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{desc}</p>
        </div>
      </div>
    </motion.div>
  );
};

export default Support;
